import React, { useState } from 'react';
import { Calendar } from './ui/calendar';
import { Card } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './ui/dialog';
import { services, availableTimeSlots } from '../mock/mockData';
import { Calendar as CalendarIcon, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import FirstConsultationForm from './FirstConsultationForm';
import { useLanguage } from '../translations/LanguageContext';

// Locale-independent date formatter (YYYY-MM-DD) — avoids timezone shifts
// by using the date's local Y/M/D components directly.
const formatDateISO = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const Booking = () => {
  const { t, language } = useLanguage();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [isFirstConsultation, setIsFirstConsultation] = useState(false);
  const [showFirstConsultForm, setShowFirstConsultForm] = useState(false);
  const [isAtHome, setIsAtHome] = useState(false);
  const [bookedTimes, setBookedTimes] = useState([]);
  
  // Regular booking form fields
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    reasonForConsultation: ''
  });

  // Fetch already-booked time slots whenever selectedDate changes
  React.useEffect(() => {
    if (!selectedDate) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const dateStr = formatDateISO(selectedDate);
    fetch(`${API_URL}/api/bookings/availability?date=${encodeURIComponent(dateStr)}`)
      .then((r) => r.ok ? r.json() : { booked_times: [] })
      .then((data) => setBookedTimes(data.booked_times || []))
      .catch(() => setBookedTimes([]));
  }, [selectedDate]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
    if (!date) setBookedTimes([]);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime || !selectedService) {
      toast.error(t.booking.messages.selectDateTime);
      return;
    }

    if (isFirstConsultation) {
      setShowFirstConsultForm(true);
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const bookingData = {
        service: selectedService,
        date: formatDateISO(selectedDate),
        time: selectedTime,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        reason_for_consultation: formData.reasonForConsultation,
        is_first_consultation: false,
        is_at_home: isAtHome
      };

      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData)
      });

      if (!response.ok) {
        if (response.status === 409) {
          toast.error(t.booking.messages.slotConflict || 'This time slot was just booked. Please pick another.');
          // Refresh booked times
          const refreshRes = await fetch(`${API_URL}/api/bookings/availability?date=${encodeURIComponent(formatDateISO(selectedDate))}`);
          if (refreshRes.ok) {
            const data = await refreshRes.json();
            setBookedTimes(data.booked_times || []);
            setSelectedTime('');
          }
          return;
        }
        throw new Error('Booking failed');
      }

      const result = await response.json();
      toast.success(t.booking.messages.bookingSuccess);
      
      // Reset form
      setFormData({ name: '', email: '', phone: '', reasonForConsultation: '' });
      setSelectedDate(null);
      setSelectedTime('');
      setSelectedService('');
      setIsFirstConsultation(false);
      setIsAtHome(false);
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to create booking. Please try again.');
    }
  };

  const handleFirstConsultationSubmit = async (consultationData) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const bookingData = {
        service: selectedService,
        date: formatDateISO(selectedDate),
        time: selectedTime,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        reason_for_consultation: formData.reasonForConsultation,
        is_first_consultation: true,
        is_at_home: isAtHome,
        consultation_data: consultationData,
        language: language
      };

      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData)
      });

      if (!response.ok) {
        if (response.status === 409) {
          toast.error(t.booking.messages.slotConflict || 'This time slot was just booked. Please pick another.');
          setShowFirstConsultForm(false);
          const refreshRes = await fetch(`${API_URL}/api/bookings/availability?date=${encodeURIComponent(formatDateISO(selectedDate))}`);
          if (refreshRes.ok) {
            const data = await refreshRes.json();
            setBookedTimes(data.booked_times || []);
            setSelectedTime('');
          }
          return;
        }
        throw new Error('Booking failed');
      }

      const result = await response.json();
      toast.success(t.booking.messages.consultationSuccess);
      
      // Reset form
      setFormData({ name: '', email: '', phone: '', reasonForConsultation: '' });
      setSelectedDate(null);
      setSelectedTime('');
      setSelectedService('');
      setIsFirstConsultation(false);
      setIsAtHome(false);
      setShowFirstConsultForm(false);
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit consultation form. Please try again.');
    }
  };

  return (
    <section id="booking" className="py-20 px-4">
      <div className="container max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="heading-2 mb-4">{t?.booking?.title || 'Book Your Appointment'}</h2>
          <p className="body-large text-secondary max-w-2xl mx-auto">
            {t?.booking?.subtitle || 'Select your preferred service, date, and time. We look forward to supporting your wellness journey.'}
          </p>
        </div>

        <Card className="product-card p-8" data-testid="booking-card">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
            {/* Service Selection — native select for max mobile compatibility */}
            <div className="space-y-2">
              <Label htmlFor="service" className="body-medium font-semibold">{t.booking.form.selectService} *</Label>
              <select
                id="service"
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                required
                data-testid="service-select-trigger"
                className="flex h-11 w-full items-center justify-between rounded-md border border-light bg-white px-3 py-2 text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-accent-primary appearance-none bg-no-repeat bg-right pr-10"
                style={{
                  backgroundImage: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
                  backgroundPosition: "right 0.75rem center",
                }}
              >
                <option value="" disabled>{t.booking.form.chooseService}</option>
                {services.map((service) => (
                  <option
                    key={service.id}
                    value={service.id}
                    data-testid={`service-option-${service.id}`}
                  >
                    {service.name} — {service.price} ({service.duration})
                  </option>
                ))}
              </select>
            </div>

            {/* First Consultation Checkbox */}
            <div>
              <div className="flex items-center space-x-2 p-4 bg-accent-wash rounded-lg">
                <Checkbox 
                  id="firstConsultation" 
                  checked={isFirstConsultation}
                  onCheckedChange={setIsFirstConsultation}
                  data-testid="first-consultation-checkbox"
                />
                <Label 
                  htmlFor="firstConsultation" 
                  className="body-medium cursor-pointer"
                >
                  {t.booking.form.firstConsultation}
                </Label>
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="body-medium font-semibold">{t.booking.form.fullName} *</Label>
                <Input 
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t.booking.form.namePlaceholder}
                  required
                  data-testid="booking-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="body-medium font-semibold">{t.booking.form.email} *</Label>
                <Input 
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t.booking.form.emailPlaceholder}
                  required
                  data-testid="booking-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="body-medium font-semibold">{t.booking.form.phone} *</Label>
              <Input 
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder={t.booking.form.phonePlaceholder}
                required
                data-testid="booking-phone-input"
              />
            </div>

            {/* Reason for Consultation (shown for Acupuncture) */}
            {selectedService === 'acupuncture' && (
              <div className="space-y-2">
                <Label htmlFor="reasonForConsultation" className="body-medium font-semibold">
                  {t.booking.form.reasonForConsultation} *
                </Label>
                <Textarea 
                  id="reasonForConsultation"
                  name="reasonForConsultation"
                  value={formData.reasonForConsultation}
                  onChange={handleInputChange}
                  placeholder={t.booking.form.reasonPlaceholder}
                  rows={4}
                  required
                  data-testid="booking-reason-textarea"
                />
              </div>
            )}

            {/* Date Selection */}
            <div className="space-y-2">
              <Label className="body-medium font-semibold flex items-center gap-2">
                <CalendarIcon size={18} />
                {t.booking.form.selectDate} *
              </Label>
              <div className="flex justify-center p-4 border border-light rounded-lg">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={(date) => {
                    const day = date.getDay();
                    const isPast = date < new Date();
                    const isSunday = day === 0;
                    const isSaturday = day === 6;
                    
                    // Always disable past dates and Sundays
                    if (isPast || isSunday) return true;
                    
                    // Only disable Saturdays if NOT "At Home"
                    if (isSaturday && !isAtHome) return true;
                    
                    return false;
                  }}
                  className="rounded-md"
                />
              </div>
            </div>

            {/* Home Care Option */}
            <div>
              <div className="flex items-center space-x-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Checkbox 
                  id="atHome" 
                  checked={isAtHome}
                  onCheckedChange={(checked) => {
                    setIsAtHome(checked);
                    // Reset selected date if it's a Saturday and unchecking "At Home"
                    if (!checked && selectedDate && selectedDate.getDay() === 6) {
                      setSelectedDate(null);
                    }
                  }}
                  data-testid="home-care-checkbox"
                />
                <Label 
                  htmlFor="atHome" 
                  className="body-medium cursor-pointer font-semibold"
                >
                  {t.booking.form.homeCare}
                </Label>
              </div>
              {isAtHome && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    ℹ️ {t.booking.form.homeCareInfo}
                  </p>
                </div>
              )}
            </div>

            {/* Time Slot Selection */}
            {selectedDate && (
              <div className="space-y-2">
                <Label className="body-medium font-semibold flex items-center gap-2">
                  <Clock size={18} />
                  {t.booking.form.selectTime} *
                </Label>
                <RadioGroup value={selectedTime} onValueChange={setSelectedTime}>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="time-slots-grid">
                    {availableTimeSlots.map((slot) => {
                      const isBooked = bookedTimes.includes(slot);
                      return (
                        <div key={slot} className="flex items-center space-x-2">
                          <RadioGroupItem
                            value={slot}
                            id={slot}
                            disabled={isBooked}
                            data-testid={`time-slot-${slot.replace(/[\s:]/g, '-')}`}
                          />
                          <Label
                            htmlFor={slot}
                            className={`body-small px-3 py-2 border rounded-full transition-colors ${
                              isBooked
                                ? 'border-light bg-gray-100 text-gray-400 line-through cursor-not-allowed'
                                : 'border-light cursor-pointer hover:bg-accent-wash'
                            }`}
                          >
                            {slot}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Submit Button */}
            <button type="submit" className="btn-primary w-full" data-testid="booking-submit-button">
              <CheckCircle size={20} className="inline mr-2" />
              {isFirstConsultation ? t.booking.form.continueToIntake : t.booking.form.confirmAppointment}
            </button>
          </form>
        </Card>

        {/* First Consultation Form Dialog */}
        <Dialog open={showFirstConsultForm} onOpenChange={setShowFirstConsultForm}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="heading-3">{t.firstConsultation.title}</DialogTitle>
              <DialogDescription className="body-small text-secondary pt-2">
                {t.firstConsultation.subtitle}
              </DialogDescription>
            </DialogHeader>
            <FirstConsultationForm 
              onSubmit={handleFirstConsultationSubmit}
              onCancel={() => setShowFirstConsultForm(false)}
            />
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default Booking;
